/**
 * ViewExportMenu — a small "Export" dropdown for the non-board representations,
 * the data-export peer of the board's image ExportShareMenu. A representation
 * passes the format actions it can produce (CSV for tabular/index, PDF for the
 * index); the menu just runs them and toasts on failure. Async actions (the
 * dynamically-imported PDF) are awaited so a failure surfaces.
 */
import type { ReactElement } from "react";
import { Menu, MenuButton, MenuItems, MenuItem, buttonClasses, useToastOptional } from "@archlang/ui";

export interface ViewExportAction {
  readonly label: string;
  readonly run: () => void | Promise<void>;
}

const itemClass =
  "block w-full px-3 py-1.5 text-left text-sm text-slate-300 hover:bg-slate-800 hover:text-slate-100 disabled:opacity-50";

export function ViewExportMenu({
  actions,
  label = "Export",
}: {
  readonly actions: readonly ViewExportAction[];
  readonly label?: string;
}): ReactElement | null {
  const toast = useToastOptional();
  if (actions.length === 0) return null;
  return (
    <div className="relative" data-hud>
      <Menu placement="bottom-end">
        <MenuButton className={buttonClasses("secondary", "sm")} aria-haspopup="menu" data-testid="view-export-menu">
          {label}
        </MenuButton>
        <MenuItems className="au-panel-in z-overlay w-44 overflow-hidden rounded-md border border-slate-700 bg-slate-900 py-1 shadow-xl">
          {actions.map((a) => (
            <MenuItem
              key={a.label}
              className={itemClass}
              onClick={() => {
                void Promise.resolve()
                  .then(a.run)
                  .catch(() => {
                    toast?.error("Export failed.");
                  });
              }}
            >
              {a.label}
            </MenuItem>
          ))}
        </MenuItems>
      </Menu>
    </div>
  );
}
